/**
 * Betreuungsakte Detailansicht - 360° Übersicht
 * 
 * Tabs:
 * 1. Übersicht - Lebenssituation auf einen Blick
 * 2. Aufgabenkreise - Zugewiesene Bereiche
 * 3. Finanzen - Einnahmen, Ausgaben, Konten, Schulden
 * 4. Aufgaben - Alle Aufgaben zur Betreuung
 * 5. Berichte - Rechnungslegung & Jahresberichte
 * 6. Vergütung - Vergütungsanträge
 * 7. Dokumente - Schriftverkehr
 */

import BeschlussKarte, { AK_KATEGORIE } from '@/components/BeschlussKarte';
import { gruppiereAufgaben, fortschritt } from '@/lib/aufgabenBaum';
import { schuldenAnzeige } from '@/lib/finanzAnzeige';
import { LebenssituationKacheln } from '../components/LebenssituationKacheln';
import { VorgaengeKacheln } from '../components/VorgaengeKacheln';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTabParams } from '@/hooks/useTabRoute';
import FallKiChat from '@/components/FallKiChat';
import DokumentePhysisch from '@/components/DokumentePhysisch';
import {
  ArrowLeft,
  Edit,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  FileText,
  Euro,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  RefreshCw,
  Home,
  Heart,
  Briefcase,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Shield,
  Building2,
  Scale,
  Receipt,
  MoreVertical,
  Trash2,
  Eye,
  Upload,
  Calculator,
  Folder,
  FolderPlus,
  FolderOpen,
  Loader2,
  ChevronRight,
  MoveRight,
  Download,
  ExternalLink,
  Share2,
  Pencil,
  Link2,
  X as XIcon,
  Check
} from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';
import { TAB_EVENTS } from '@/lib/tabEvents';
import VorlagenBibliothek from '@/components/VorlagenBibliothek';
import { schreibstubeAPI } from '@/api/schreibstubeAPI';
import { schreibstubeUrl } from '@/pages/schreibstube/schreibstube';
import VerbindungenTab from '@/pages/betreuung/VerbindungenTab';
import MassnahmeDetailDialog from '@/components/MassnahmeDetailDialog';
import { useAuth } from '@/contexts/AuthContext';

// Status-Farben
const STATUS_COLORS = {
  aktiv: 'bg-green-100 text-green-700',
  ruhend: 'bg-yellow-100 text-yellow-700',
  beendet: 'bg-slate-100 text-slate-700',
};

const PRIORITAET_COLORS = {
  niedrig: 'bg-green-500 text-white',
  mittel: 'bg-yellow-400 text-black',
  hoch: 'bg-red-500 text-white',
  dringend: 'bg-red-700 text-white',
};
const PRIORITAET_LABELS = {
  niedrig: 'Niedrig',
  mittel: 'Normal',
  hoch: 'Dringend',
  dringend: 'Sehr dringend',
};

export default function BetreuungsakteDetailPage() {
  return (
    <BetreuungsakteErrorBoundary>
      <BetreuungsakteDetailContent />
    </BetreuungsakteErrorBoundary>
  );
}

class BetreuungsakteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <AlertCircle className="w-12 h-12 text-red-400" />
          <h2 className="text-lg font-semibold text-slate-700">Fehler beim Laden der Akte</h2>
          <p className="text-sm text-slate-500 max-w-md text-center">
            Diese Betreuungsakte enthält möglicherweise fehlerhafte Daten. 
            Der Datensatz könnte beschädigt oder unvollständig sein.
          </p>
          <p className="text-xs text-red-400 font-mono max-w-lg text-center">{this.state.error?.message}</p>
          <div className="flex gap-3">
            <a href="/akten" className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
              Zurück zur Übersicht
            </a>
            <button onClick={() => this.setState({ hasError: false, error: null })} className="px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition-colors">
              Erneut versuchen
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function BetreuungsakteDetailContent() {
  const { id } = useTabParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uebersicht');
  
  // Daten
  const [klient, setKlient] = useState(null);
  const [uebersicht, setUebersicht] = useState(null);
  const [aufgabenkreiseKatalog, setAufgabenkreiseKatalog] = useState([]);
  
  // Dialoge → durch Tab-Öffnung ersetzt
  const [massnahmeDialog, setMassnahmeDialog] = useState({ open: false, data: null });
  
  const { openTab: openTabFn, activeTabId, updateTabTitle } = useTabs();
  const { user } = useAuth();
  const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
  
  // Aktensperren
  const [lockInfo, setLockInfo] = useState(null); // { locked_by, locked_at }
  const [aktivityLog, setAktivityLog] = useState([]);
  const [showActivityLog, setShowActivityLog] = useState(false);
  
  // Jahresbericht
  const [berichtLoading, setBerichtLoading] = useState(false);
  const [berichtResult, setBerichtResult] = useState(null);
  const [showBerichtDialog, setShowBerichtDialog] = useState(false);

  // Schriftverkehr
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefZusatzInfo, setBriefZusatzInfo] = useState('');
  const [showVorlagenBibliothek, setShowVorlagenBibliothek] = useState(false);
  
  // Hilfsfunktion: Betreuungsakte als Tab registrieren + Kind-Tab öffnen
  const openChildTab = (path, options) => {
    openTabFn(`/akten/${id}`, { title: klient ? `${klient.vorname} ${klient.nachname}` : 'Akte' });
    openTabFn(path, options);
    navigate(path);
  };
  
  // Dokumente & Ordner
  const [ordner, setOrdner] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [selectedOrdner, setSelectedOrdner] = useState(null);
  const [loadingDokumente, setLoadingDokumente] = useState(false);
  const [ordnerBreadcrumbs, setOrdnerBreadcrumbs] = useState([]);
  const [selectedDokumente, setSelectedDokumente] = useState([]);
  const [neuerOrdnerDialog, setNeuerOrdnerDialog] = useState({ open: false });
  const [moveDialog, setMoveDialog] = useState({ open: false, targetOrdner: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false });
  const [renamingDok, setRenamingDok] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renamingOrdner, setRenamingOrdner] = useState(null);
  const [renameOrdnerValue, setRenameOrdnerValue] = useState('');

  // Dokumente und Ordner laden
  const loadDokumenteUndOrdner = async (ordnerId = null) => {
    setLoadingDokumente(true);
    try {
      const [ordnerData, dokumenteData] = await Promise.all([
        betreuungAPI.listOrdner({ 
          klient_id: id, 
          parent_id: ordnerId || '', 
          include_counts: true 
        }),
        betreuungAPI.listDokumente({ 
          klient_id: id,
          ordner_id: ordnerId || ''
        })
      ]);
      setOrdner(ordnerData || []);
      setDokumente(dokumenteData || []);
      setSelectedOrdner(ordnerId);
      setSelectedDokumente([]);
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
      toast.error('Fehler beim Laden der Dokumente');
    } finally {
      setLoadingDokumente(false);
    }
  };

  // In Ordner navigieren
  const navigateToOrdner = async (ordnerId, ordnerName = null) => {
    if (ordnerId) {
      // Breadcrumb hinzufügen
      setOrdnerBreadcrumbs(prev => [...prev, { id: ordnerId, name: ordnerName }]);
    } else {
      // Zurück zur Wurzel
      setOrdnerBreadcrumbs([]);
    }
    await loadDokumenteUndOrdner(ordnerId);
  };

  // Breadcrumb-Navigation
  const navigateToBreadcrumb = async (index) => {
    if (index < 0) {
      // Wurzel
      setOrdnerBreadcrumbs([]);
      await loadDokumenteUndOrdner(null);
    } else {
      const newBreadcrumbs = ordnerBreadcrumbs.slice(0, index + 1);
      setOrdnerBreadcrumbs(newBreadcrumbs);
      await loadDokumenteUndOrdner(newBreadcrumbs[index].id);
    }
  };

  // Dokument umbenennen
  const handleRenameDok = async (dokId) => {
    if (!renameValue.trim()) return;
    try {
      await betreuungAPI.renameDokument(dokId, renameValue.trim());
      toast.success('Dokument umbenannt');
      setRenamingDok(null);
      setRenameValue('');
      loadDokumenteUndOrdner(selectedOrdner);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Ordner umbenennen
  const handleRenameOrdner = async (ordnerId) => {
    if (!renameOrdnerValue.trim()) return;
    try {
      await betreuungAPI.renameOrdner(ordnerId, renameOrdnerValue.trim());
      toast.success('Ordner umbenannt');
      setRenamingOrdner(null);
      setRenameOrdnerValue('');
      loadDokumenteUndOrdner(selectedOrdner);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Ordner löschen
  const handleDeleteOrdner = async (ordnerId, ordnerName) => {
    if (!window.confirm(`Ordner "${ordnerName}" wirklich löschen? Dokumente werden ins Stammverzeichnis verschoben.`)) return;
    try {
      await betreuungAPI.deleteOrdner(ordnerId, 'none');
      toast.success('Ordner gelöscht');
      loadDokumenteUndOrdner(selectedOrdner);
    } catch (err) {
      toast.error(err.message);
    }
  };


  const loadData = async () => {
    setLoading(true);
    try {
      const [uebersichtData, katalog] = await Promise.all([
        betreuungAPI.getKlientUebersicht(id),
        betreuungAPI.getAufgabenkreiseKatalog()
      ]);
      
      // Fallback für fehlende Daten
      const safeUebersicht = {
        klient: uebersichtData.klient || {},
        aufgabenkreise: uebersichtData.aufgabenkreise || [],
        klient_aufgabenkreise: uebersichtData.klient_aufgabenkreise || [],
        betreuung: uebersichtData.betreuung || null,
        betreuung_leistungen: uebersichtData.betreuung_leistungen || [],
        einnahmen_vorgaenge: uebersichtData.einnahmen_vorgaenge || [],
        finanzen: uebersichtData.finanzen || {
          einnahmen: [],
          ausgaben: [],
          versicherungen: [],
          bankkonten: [],
          betreuung_konten: [],
          glaeubigerverbindungen: [],
          versorgung: [],
          statistik: {
            einnahmen_monatlich: 0,
            ausgaben_monatlich: 0,
            schulden_gesamt: 0,
            kontostand_gesamt: 0,
          }
        },
        aufgaben: uebersichtData.aufgaben || [],
        verguetung: uebersichtData.verguetung || [],
        rechnungslegungen: uebersichtData.rechnungslegungen || [],
      };
      
      setUebersicht(safeUebersicht);
      setKlient(safeUebersicht.klient);
      setAufgabenkreiseKatalog(katalog || []);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Betreuungsakte nicht gefunden');
      navigate('/akten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadData();
      // Tracking: Als zuletzt angesehen markieren
      fetch(`${BACKEND_URL}/api/zuletzt-angesehen/${id}`, { method: 'POST' })
        .catch(err => console.error('Zuletzt angesehen tracking Fehler:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Tab-Titel mit Klient-Name aktualisieren
  useEffect(() => {
    if (klient && activeTabId) {
      const name = `${klient.vorname || ''} ${klient.nachname || ''}`.trim();
      if (name) updateTabTitle(activeTabId, `Akte: ${name}`);
    }
  }, [klient, activeTabId, updateTabTitle]);

  // Cross-Tab Event Listener: Daten aktualisieren wenn in anderen Tabs Entities erstellt werden
  useEffect(() => {
    const handleRefresh = (e) => {
      if (!e.detail?.scope || e.detail.scope === 'betreuungsakte') loadData();
    };
    window.addEventListener(TAB_EVENTS.DATA_REFRESH, handleRefresh);
    return () => window.removeEventListener(TAB_EVENTS.DATA_REFRESH, handleRefresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Dokumente laden wenn Tab gewechselt wird
  useEffect(() => {
    if (activeTab === 'dokumente' && id) {
      loadDokumenteUndOrdner(selectedOrdner);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  // ==================== AKTENSPERREN ====================
  useEffect(() => {
    if (!id || !user) return;
    
    const acquireLock = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/locks/acquire`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ akte_id: id, user_id: user.id || user.email, user_name: user.name || user.email })
        });
        const data = await res.json();
        if (data.locked && !data.own_lock) {
          setLockInfo({ locked_by: data.locked_by, locked_at: data.locked_at });
        } else {
          setLockInfo(null);
        }
      } catch {}
    };
    
    acquireLock();
    
    // Heartbeat alle 60 Sekunden
    const heartbeatInterval = setInterval(async () => {
      try {
        await fetch(`${BACKEND_URL}/api/locks/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ akte_id: id, user_id: user.id || user.email })
        });
      } catch {}
    }, 60000);
    
    // Lock freigeben beim Verlassen
    return () => {
      clearInterval(heartbeatInterval);
      fetch(`${BACKEND_URL}/api/locks/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ akte_id: id, user_id: user.id || user.email })
      }).catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  // Aktivitätslog laden
  const loadActivityLog = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/locks/activity/${id}?limit=30`);
      const data = await res.json();
      if (data.success) setAktivityLog(data.entries || []);
    } catch {}
  };

  // Änderung protokollieren
  const logActivity = async (feld, alterWert, neuerWert) => {
    if (!user || !id) return;
    try {
      await fetch(`${BACKEND_URL}/api/locks/activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          akte_id: id,
          user_id: user.id || user.email,
          user_name: user.name || user.email,
          feld, alter_wert: alterWert, neuer_wert: neuerWert
        })
      });
    } catch {}
  };

  // Jahresbericht generieren
  const handleGenerateBericht = async (typ = 'jahresbericht') => {
    setBerichtLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/berichte/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ klient_id: id, bericht_typ: typ })
      });
      const data = await res.json();
      if (data.success) {
        setBerichtResult(data);
        setShowBerichtDialog(true);
        toast.success(`${typ === 'jahresbericht' ? 'Jahresbericht' : 'Rechnungslegung'} erstellt`);
      } else {
        toast.error(data.error || 'Generierung fehlgeschlagen');
      }
    } catch { toast.error('Fehler bei der Generierung'); }
    finally { setBerichtLoading(false); }
  };

  const handleExportBerichtDocx = async (berichtId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/berichte/export-docx/${berichtId}`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `Bericht_${berichtId}.docx`; a.click();
        URL.revokeObjectURL(url);
        toast.success('Word-Dokument heruntergeladen');
      }
    } catch { toast.error('DOCX-Export fehlgeschlagen'); }
  };

  // Schriftverkehr generieren
  // Korrespondenz (25.10.2026): erzeugte Briefe (KI-Vorlage / Vorlagenbibliothek) werden im EINEN Arbeitsplatz weiterbearbeitet und versendet
  const inKorrespondenz = async (schriftverkehrId) => {
    try {
      const b = await schreibstubeAPI.altUebernehmen(schriftverkehrId);
      (b.uebernahme_hinweise || []).forEach((h) => toast.info(h));
      openTabFn(schreibstubeUrl({ brief: b.id }), { title: 'Korrespondenz' });
    } catch (e) { toast.error(e.message || 'Übergabe an die Korrespondenz fehlgeschlagen'); }
  };

  const handleGenerateBrief = async (briefTyp) => {
    setBriefLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/schriftverkehr/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ klient_id: id, brief_typ: briefTyp, zusatz_info: briefZusatzInfo || null })
      });
      const data = await res.json();
      if (data.success) {
        setBriefZusatzInfo('');
        toast.success(`${data.brief_label} erstellt – in der Korrespondenz geöffnet`);
        await inKorrespondenz(data.brief_id);
      } else {
        toast.error(data.error || 'Generierung fehlgeschlagen');
      }
    } catch { toast.error('Fehler bei der Brief-Generierung'); }
    finally { setBriefLoading(false); }
  };

  // Vorlage wurde angewendet
  const handleVorlageApplied = (brief) => {
    setShowVorlagenBibliothek(false);
    inKorrespondenz(brief.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!klient) return null;

  const { finanzen, aufgabenkreise, klient_aufgabenkreise, betreuung_leistungen, einnahmen_vorgaenge, aufgaben, verguetung, rechnungslegungen } = uebersicht;
  // Unteraufgaben (Checkliste) erscheinen unter ihrer Hauptaufgabe, nicht als eigene Aufgaben
  const aufgabenBaum = gruppiereAufgaben(aufgaben || []);
  
  // Aufgabenkreise: DB-Collection oder Klient-Feld als Fallback
  const effectiveAufgabenkreise = aufgabenkreise.length > 0 
    ? aufgabenkreise 
    : (klient_aufgabenkreise || []).map(ak => ({ bezeichnung: ak, aktiv: true }));
  
  // Betreuung-Konten aus finanzen
  const betreuungKonten = finanzen?.betreuung_konten || [];

  return (
    <div className="space-y-6" data-testid="betreuungsakte-detail">
      {/* Lock-Warnung */}
      {lockInfo && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="lock-warning-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              Akte wird gerade von <span className="font-bold">{lockInfo.locked_by}</span> bearbeitet
            </p>
            <p className="text-xs text-amber-600">
              Seit {new Date(lockInfo.locked_at).toLocaleTimeString('de-DE')} · Ihre Änderungen könnten überschrieben werden
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/akten')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-800">
                {klient.vorname} {klient.nachname}
              </h1>
              <Badge className={STATUS_COLORS[klient.status] || STATUS_COLORS.aktiv}>
                {klient.status || 'aktiv'}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              {klient.fallnummer && <span>Az: {klient.fallnummer}</span>}
              {klient.geburtsdatum && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(klient.geburtsdatum).toLocaleDateString('de-DE')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={berichtLoading} data-testid="jahresbericht-btn">
                {berichtLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Bericht
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleGenerateBericht('jahresbericht')}>
                Jahresbericht (§ 1863 BGB)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateBericht('rechnungslegung')}>
                Rechnungslegung (§ 1865 BGB)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={briefLoading} data-testid="schriftverkehr-btn">
                {briefLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                Brief erstellen
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => openTabFn(schreibstubeUrl({ klient: id }), { title: 'Korrespondenz' })} data-testid="brief-neu-korrespondenz">
                Neues Schreiben (Korrespondenz)
              </DropdownMenuItem>
              <DropdownMenuItem className="border-t mt-1 pt-1" onClick={() => handleGenerateBrief('antrag_pflegegrad')} data-testid="brief-pflegegrad">
                Antrag Pflegegrad
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateBrief('widerspruch_pflegekasse')} data-testid="brief-widerspruch">
                Widerspruch Pflegekasse
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateBrief('anfrage_sozialamt')} data-testid="brief-sozialamt">
                Anfrage Sozialamt
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleGenerateBrief('genehmigung_betreuungsgericht')} data-testid="brief-gericht">
                Genehmigungsantrag Gericht
              </DropdownMenuItem>
              <DropdownMenuItem className="border-t mt-1 pt-1" onClick={() => setShowVorlagenBibliothek(true)} data-testid="vorlagen-bibliothek-btn">
                <Folder className="w-4 h-4 mr-2 text-orange-500" />
                Aus Vorlage erstellen...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={() => { setShowActivityLog(v => !v); if (!showActivityLog) loadActivityLog(); }}
            data-testid="activity-log-btn">
            <Clock className="w-4 h-4 mr-1" />
            Aktivitätslog
          </Button>
          <Button variant="outline" size="sm" onClick={() => openChildTab(`/akten/${id}/blick`, { title: `${klient.vorname} ${klient.nachname} - Blick` })}
            data-testid="akte-blick-btn">
            <Sparkles className="w-4 h-4 mr-1" />
            Auf einen Blick
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
          <Link to={`/akten/${id}/bearbeiten`}>
            <Button>
              <Edit className="w-4 h-4 mr-2" />
              Bearbeiten
            </Button>
          </Link>
        </div>
      </div>

      {/* Aktivitätslog Panel */}
      {showActivityLog && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="activity-log-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              Letzte Änderungen
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setShowActivityLog(false)}>
              <ExternalLink className="w-3 h-3" />
            </Button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1.5 text-xs">
            {aktivityLog.length === 0 ? (
              <p className="text-slate-400 text-center py-3">Keine Aktivitäten protokolliert</p>
            ) : aktivityLog.map(entry => (
              <div key={entry.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50">
                <span className="text-slate-400 shrink-0 w-14">{new Date(entry.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                <span className="font-medium text-violet-700 shrink-0">{entry.user_name}</span>
                <span className="text-slate-600 truncate">
                  {entry.feld}{entry.neuer_wert ? `: ${entry.neuer_wert.substring(0, 50)}` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-auto flex w-full overflow-x-auto no-scrollbar p-1 gap-1" data-testid="detail-tabs">
          <TabsTrigger value="uebersicht" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Übersicht</TabsTrigger>
          <TabsTrigger value="aufgabenkreise" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Aufgabenkreise</TabsTrigger>
          <TabsTrigger value="finanzen" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Finanzen</TabsTrigger>
          <TabsTrigger value="aufgaben" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Aufgaben</TabsTrigger>
          <TabsTrigger value="berichte" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Berichte</TabsTrigger>
          <TabsTrigger value="verguetung" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Vergütung</TabsTrigger>
          <TabsTrigger value="dokumente" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0">Dokumente</TabsTrigger>
          <TabsTrigger value="verbindungen" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0 flex items-center gap-1" data-testid="tab-verbindungen">
            <Share2 className="w-3.5 h-3.5" />Verbindungen
          </TabsTrigger>
          <TabsTrigger value="ki-berater" className="text-xs sm:text-sm px-2 sm:px-3 shrink-0 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5" />KI-Berater
          </TabsTrigger>
        </TabsList>

        {/* Tab: Übersicht */}
        <TabsContent value="uebersicht" className="space-y-6">
          {/* Mini-Übersicht: Kritische Punkte auf einen Blick */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Aktuelle Vorgänge */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-blue-500" />
                    Aktuelle Vorgänge
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setActiveTab('finanzen')} title="Vorgänge verwalten">
                    <Edit className="w-3 h-3 text-slate-400" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {[...finanzen.einnahmen, ...finanzen.versicherungen].filter(m => m.status === 'aktiv').length > 0 ? (
                  <div className="space-y-1.5">
                    {[...finanzen.einnahmen, ...finanzen.versicherungen]
                      .filter(m => m.status === 'aktiv')
                      .slice(0, 4)
                      .map(m => (
                        <div key={m.id} className="flex items-center justify-between text-sm cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 transition-colors"
                          onClick={() => setMassnahmeDialog({ open: true, data: m })}
                          data-testid={`vorgang-${m.id}`}
                        >
                          <span className="text-slate-700 truncate">{m.bezeichnung || m.typ}</span>
                          <div className="flex items-center gap-1">
                            {m.betrag > 0 && <span className="font-medium text-slate-800 shrink-0 ml-2">{m.betrag.toLocaleString('de-DE')} €</span>}
                            <ChevronRight className="w-3 h-3 text-slate-300" />
                          </div>
                        </div>
                      ))}
                    {[...finanzen.einnahmen, ...finanzen.versicherungen].filter(m => m.status === 'aktiv').length > 4 && (
                      <button onClick={() => setActiveTab('finanzen')} className="text-xs text-blue-600 hover:underline">
                        + {[...finanzen.einnahmen, ...finanzen.versicherungen].filter(m => m.status === 'aktiv').length - 4} weitere
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-orange-600">Keine Vorgänge erfasst</p>
                )}
              </CardContent>
            </Card>

            {/* Bankverbindung */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    Bankverbindung
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setActiveTab('finanzen')} title="Bankverbindung verwalten">
                    <Edit className="w-3 h-3 text-slate-400" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(finanzen.bankkonten.length > 0 || betreuungKonten.length > 0) ? (
                  <div className="space-y-2">
                    {betreuungKonten.map(k => (
                      <div key={k.id} className="text-sm" data-testid={`bankkonto-${k.id}`}>
                        <p className="font-medium text-slate-800">{k.bezeichnung}</p>
                        <p className="text-slate-500 text-xs">{k.bank}{k.iban ? ` · ${k.iban}` : ''}</p>
                        {(k.saldo !== null && k.saldo !== undefined) && (
                          <p className="text-slate-700 mt-0.5">Saldo: <span className="font-medium">{Number(k.saldo).toLocaleString('de-DE')} €</span></p>
                        )}
                      </div>
                    ))}
                    {finanzen.bankkonten.map(k => (
                      <div key={k.id} className="text-sm">
                        <p className="font-medium text-slate-800">{k.bezeichnung || k.typ}</p>
                        {k.iban && <p className="text-slate-500 font-mono text-xs mt-0.5">{k.iban}</p>}
                        {(k.kontostand !== null && k.kontostand !== undefined) && (
                          <p className="text-slate-700 mt-0.5">Saldo: <span className="font-medium">{k.kontostand.toLocaleString('de-DE')} €</span></p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : klient.girokonto_iban ? (
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">{klient.girokonto_bank || 'Girokonto'}</p>
                    <p className="text-slate-500 font-mono text-xs mt-0.5">{klient.girokonto_iban}</p>
                  </div>
                ) : (
                  <p className="text-sm text-orange-600">Keine Bankverbindung erfasst</p>
                )}
              </CardContent>
            </Card>

            {/* Aufgabenkreise Kurzinfo */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-slate-500" />
                    Aufgabenkreise
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setActiveTab('aufgabenkreise')} title="Aufgabenkreise verwalten">
                    <Edit className="w-3 h-3 text-slate-400" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {effectiveAufgabenkreise.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {effectiveAufgabenkreise.map((ak, idx) => (
                      <Badge key={ak.id || idx} variant="secondary" className="text-xs">{ak.name || ak.bezeichnung}</Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-red-600">Keine Aufgabenkreise erfasst!</p>
                )}
              </CardContent>
            </Card>

            {/* Kennzahlen */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-500" />
                  Kennzahlen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Offene Aufgaben</p>
                    <p className={`text-lg font-bold ${aufgabenBaum.length > 0 ? 'text-orange-600' : 'text-green-600'}`}>{aufgabenBaum.length}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Schulden</p>
                    {/* 02.11.2026: ohne Glaeubigereintrag keine gruene 0 -- "keine Einträge" ist kein Nachweis, schuldenfrei zu sein */}
                    {schuldenAnzeige(finanzen.statistik).betrag !== null ? (
                      <p className="text-lg font-bold text-red-600" data-testid="akte-schulden">{schuldenAnzeige(finanzen.statistik).betrag}</p>
                    ) : (
                      <p className="text-sm font-medium text-slate-500" data-testid="akte-schulden" title={schuldenAnzeige(finanzen.statistik).hinweis}>{schuldenAnzeige(finanzen.statistik).text}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Lebenssituation - Klickbare Kacheln */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Lebenssituation
                  </CardTitle>
                  <Link to={`/akten/${id}/bearbeiten`}>
                    <Button variant="ghost" size="sm" title="Stammdaten bearbeiten">
                      <Edit className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <LebenssituationKacheln
                  klient={klient}
                  finanzen={finanzen}
                  betreuungLeistungen={betreuung_leistungen}
                  einnahmenVorgaenge={einnahmen_vorgaenge}
                  klientId={id}
                  onKlientUpdate={(updated) => {
                    setKlient(prev => ({ ...prev, ...updated }));
                  }}
                />
              </CardContent>
            </Card>

            {/* Aufgabenkreise */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="w-5 h-5" />
                  Aufgabenkreise
                </CardTitle>
              </CardHeader>
              <CardContent>
                {effectiveAufgabenkreise.length > 0 ? (
                  <div className="space-y-2">
                    {effectiveAufgabenkreise.map((ak, idx) => (
                      <div key={ak.id || idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>{ak.name || ak.bezeichnung}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Keine Aufgabenkreise erfasst</p>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-4"
                  onClick={() => setActiveTab('aufgabenkreise')}
                >
                  Aufgabenkreise verwalten
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Vorgänge - Klickbare Kacheln mit Dokumenten-Ordnern */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Vorgänge
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VorgaengeKacheln
                klientId={id}
                onRefresh={() => loadData()}
              />
            </CardContent>
          </Card>

          {/* Offene Aufgaben */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Offene Aufgaben
                </CardTitle>
                <Link to={`/aufgaben/neu?klient=${id}`}>
                  <Button size="sm" className="w-full sm:w-auto">
                    <Plus className="w-4 h-4 mr-2" />
                    Neue Aufgabe
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {aufgaben.length > 0 ? (
                <div className="space-y-3">
                  {aufgabenBaum.slice(0, 5).map(({ aufgabe, kinder }) => (
                    <Link 
                      key={aufgabe.id} 
                      to={`/aufgaben/${aufgabe.id}`}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                      data-testid={`aufgabe-link-${aufgabe.id}`}
                    >
                      <div>
                        <p className="font-medium">{aufgabe.titel}</p>
                        {kinder.length > 0 && <p className="text-xs text-blue-700" data-testid={`aufgabe-fortschritt-${aufgabe.id}`}>{fortschritt(kinder, aufgabe).text}</p>}
                        {aufgabe.faelligkeit && (
                          <p className="text-sm text-slate-500">
                            Fällig: {new Date(aufgabe.faelligkeit).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                      <Badge className={PRIORITAET_COLORS[aufgabe.prioritaet] || ''}>
                        {PRIORITAET_LABELS[aufgabe.prioritaet] || aufgabe.prioritaet}
                      </Badge>
                    </Link>
                  ))}
                  {aufgabenBaum.length > 5 && (
                    <Button variant="ghost" size="sm" className="w-full text-blue-600" onClick={() => setActiveTab('aufgaben')} data-testid="akte-aufgaben-weitere">
                      +{aufgabenBaum.length - 5} weitere anzeigen
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine offenen Aufgaben</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Aufgabenkreise */}
        <TabsContent value="aufgabenkreise" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <p className="text-slate-500">
              Zugewiesene Aufgabenkreise gemäß Betreuungsbeschluss
            </p>
            <Button className="w-full sm:w-auto" onClick={() => openChildTab('/schnellerfassung/aufgabenkreis', { title: 'Aufgabenkreis', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })}>
              <Plus className="w-4 h-4 mr-2" />
              Aufgabenkreis hinzufügen
            </Button>
          </div>

          <BeschlussKarte betreuung={uebersicht.betreuung} onGespeichert={loadData} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {effectiveAufgabenkreise.length > 0 ? (
              effectiveAufgabenkreise.map((ak, idx) => (
                <Card key={ak.id || idx}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{ak.name || ak.bezeichnung}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Entfernen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent className="text-sm text-slate-600 space-y-2">
                    {ak.ist_freitext && <p className="text-xs text-slate-500" data-testid="ak-wortlaut">Wortlaut laut Gerichtsbeschluss</p>}
                    {ak.key && <span className="inline-block text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5" data-testid="ak-kategorie">Kategorie: {AK_KATEGORIE[ak.key] || ak.key}</span>}
                    {ak.gueltig_ab && (
                      <p>Seit: {new Date(ak.gueltig_ab).toLocaleDateString('de-DE')}</p>
                    )}
                    {ak.einschraenkungen && (
                      <p className="text-orange-600">Einschränkung: {ak.einschraenkungen}</p>
                    )}
                    {ak.beschluss_aktenzeichen && (
                      <p className="text-slate-500">Az: {ak.beschluss_aktenzeichen}</p>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="col-span-2">
                <CardContent className="py-8 text-center">
                  <Scale className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Keine Aufgabenkreise erfasst</p>
                  <Button 
                    className="mt-4"
                    onClick={() => openChildTab('/schnellerfassung/aufgabenkreis', { title: 'Aufgabenkreis', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Aufgabenkreis hinzufügen
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Tab: Finanzen */}
        <TabsContent value="finanzen" className="space-y-6">
          {/* Buchhaltung-Schnellzugriff */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Euro className="w-5 h-5" />
                  Buchhaltung & Kontoauszüge
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/buchhaltung`)}>
                    Zur Buchhaltung
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <BuchhaltungMini klientId={id} />
            </CardContent>
          </Card>

          {/* Einnahmen */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <TrendingUp className="w-5 h-5" />
                  Einnahmen
                </CardTitle>
                <Button size="sm" className="w-full sm:w-auto" onClick={() => setMassnahmeDialog({ open: true, data: { kategorie: 'einnahme' } })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {finanzen.einnahmen.length > 0 ? (
                <div className="space-y-3">
                  {finanzen.einnahmen.map(e => (
                    <div key={e.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => setMassnahmeDialog({ open: true, data: e })}
                      data-testid={`einnahme-${e.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{e.bezeichnung || e.typ}</p>
                        {e.institution && <p className="text-sm text-slate-500">{e.institution}</p>}
                        {e.aktenzeichen && <p className="text-xs text-slate-400">Az: {e.aktenzeichen}</p>}
                        {e.dokumente_count > 0 && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {e.dokumente_count} Dok.
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <p className="font-bold text-green-700 whitespace-nowrap">{(e.betrag || 0).toLocaleString('de-DE')} € / mtl.</p>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Einnahmen erfasst</p>
              )}
            </CardContent>
          </Card>

          {/* Bankkonten */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <CreditCard className="w-5 h-5" />
                  Bankkonten
                </CardTitle>
                <Button size="sm" className="w-full sm:w-auto" onClick={() => setMassnahmeDialog({ open: true, data: { kategorie: 'bankkonto' } })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {finanzen.bankkonten.length > 0 ? (
                <div className="space-y-3">
                  {finanzen.bankkonten.map(k => (
                    <div key={k.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => setMassnahmeDialog({ open: true, data: k })}
                      data-testid={`bankkonto-click-${k.id}`}
                    >
                      <div>
                        <p className="font-medium">{k.bezeichnung || k.typ}</p>
                        {k.iban && <p className="text-sm text-slate-500 font-mono">{k.iban}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-blue-700">{(k.kontostand || 0).toLocaleString('de-DE')} €</p>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Bankkonten erfasst</p>
              )}
            </CardContent>
          </Card>

          {/* Ausgaben */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <TrendingDown className="w-5 h-5" />
                  Regelmäßige Ausgaben
                </CardTitle>
                <Button size="sm" className="w-full sm:w-auto" onClick={() => setMassnahmeDialog({ open: true, data: { kategorie: 'ausgabe' } })}>
                  <Plus className="w-4 h-4 mr-2" />
                  Hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {finanzen.ausgaben.length > 0 ? (
                <div className="space-y-3">
                  {finanzen.ausgaben.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                      onClick={() => setMassnahmeDialog({ open: true, data: a })}
                      data-testid={`ausgabe-${a.id}`}
                    >
                      <div>
                        <p className="font-medium">{a.bezeichnung || a.typ}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-red-700">{(a.betrag || 0).toLocaleString('de-DE')} €</p>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Ausgaben erfasst</p>
              )}
            </CardContent>
          </Card>

          {/* Gläubiger */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertCircle className="w-5 h-5" />
                  Gläubigerverbindungen / Schulden
                </CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openTabFn(`/schulden-dashboard?klient=${id}`, 'Schulden-Dashboard')} data-testid="open-schulden-dashboard">
                    <Scale className="w-4 h-4 mr-1" /> Schulden-Dashboard
                  </Button>
                  <Button size="sm" className="w-full sm:w-auto" onClick={() => setMassnahmeDialog({ open: true, data: { kategorie: 'glaeubigerverbindung' } })}>
                    <Plus className="w-4 h-4 mr-2" />
                    Hinzufügen
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {finanzen.glaeubigerverbindungen.length > 0 ? (
                <div className="space-y-3">
                  {finanzen.glaeubigerverbindungen.map(g => (
                    <div key={g.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
                      onClick={() => openTabFn(`/schulden-dashboard?klient=${id}`, 'Schulden-Dashboard')}
                    >
                      <div>
                        <p className="font-medium">{g.bezeichnung || 'Gläubiger'}</p>
                        {g.aktenzeichen && <p className="text-sm text-slate-500">Az: {g.aktenzeichen}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-orange-700">{(g.schuldenstand || 0).toLocaleString('de-DE')} EUR</p>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </div>
                  ))}
                  {/* Gesamtschulden */}
                  <div className="flex items-center justify-between p-3 bg-red-100 rounded-lg border border-red-200" data-testid="schulden-gesamt">
                    <p className="font-bold text-red-800">Gesamtschulden</p>
                    <p className="font-bold text-red-700 text-lg">
                      {finanzen.glaeubigerverbindungen.reduce((sum, g) => sum + (g.schuldenstand || 0), 0).toLocaleString('de-DE')} EUR
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Schulden erfasst</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Aufgaben */}
        <TabsContent value="aufgaben" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <p className="text-slate-500">
              Alle Aufgaben zu dieser Betreuung
            </p>
            <Link to={`/aufgaben/neu?klient=${id}`}>
              <Button className="w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Neue Aufgabe
              </Button>
            </Link>
          </div>

          <Card>
            <CardContent className="pt-6">
              {aufgaben.length > 0 ? (
                <div className="space-y-3">
                  {aufgabenBaum.map(({ aufgabe, kinder }) => (
                    <div key={aufgabe.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-slate-50 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{aufgabe.titel}</p>
                          <Badge className={PRIORITAET_COLORS[aufgabe.prioritaet] || ''}>
                            {PRIORITAET_LABELS[aufgabe.prioritaet] || aufgabe.prioritaet}
                          </Badge>
                          {kinder.length > 0 && <Badge variant="outline" className="text-xs">{fortschritt(kinder, aufgabe).text}</Badge>}
                        </div>
                        {aufgabe.beschreibung && (
                          <p className="text-sm text-slate-500 mt-1">{aufgabe.beschreibung}</p>
                        )}
                        {aufgabe.faelligkeit && (
                          <p className="text-sm text-slate-500 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Fällig: {new Date(aufgabe.faelligkeit).toLocaleDateString('de-DE')}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" title="Anschreiben erstellen"
                          onClick={(e) => {
                            e.preventDefault();
                            openTabFn(`/schnellerfassung/schriftstueck-senden?klient=${id}&betreff=${encodeURIComponent(aufgabe.titel)}`, 'Anschreiben');
                          }}
                          data-testid={`aufgabe-brief-${aufgabe.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Link to={`/aufgaben/${aufgabe.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Keine offenen Aufgaben</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Berichte */}
        <TabsContent value="berichte" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <p className="text-slate-500">
              Rechnungslegungen und Jahresberichte ans Gericht
            </p>
            <Button className="w-full sm:w-auto" onClick={() => openChildTab('/schnellerfassung/bericht', { title: 'Neuer Bericht', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })} data-testid="neuer-bericht-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Bericht
            </Button>
          </div>

          {/* Statistik-Karten */}
          {rechnungslegungen.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="bg-slate-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-slate-700">{rechnungslegungen.length}</p>
                  <p className="text-xs text-slate-500">Gesamt</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{rechnungslegungen.filter(b => b.status === 'entwurf').length}</p>
                  <p className="text-xs text-amber-600">Entwürfe</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{rechnungslegungen.filter(b => b.status === 'eingereicht').length}</p>
                  <p className="text-xs text-blue-600">Eingereicht</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{rechnungslegungen.filter(b => b.status === 'genehmigt').length}</p>
                  <p className="text-xs text-green-600">Genehmigt</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardContent className="pt-6">
              {rechnungslegungen.length > 0 ? (
                <div className="space-y-3">
                  {rechnungslegungen.map(bericht => (
                    <div key={bericht.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors group" data-testid={`bericht-${bericht.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">
                          {bericht.typ === 'jahresbericht' ? 'Jahresbericht' : bericht.typ === 'rechnungslegung' ? 'Rechnungslegung' : bericht.typ === 'schlussbericht' ? 'Schlussbericht' : bericht.typ === 'anfangsbericht' ? 'Anfangsbericht' : bericht.typ} {bericht.berichtsjahr}
                        </p>
                        <p className="text-sm text-slate-500">
                          Zeitraum: {bericht.zeitraum_von ? new Date(bericht.zeitraum_von).toLocaleDateString('de-DE') : '?'} - {bericht.zeitraum_bis ? new Date(bericht.zeitraum_bis).toLocaleDateString('de-DE') : '?'}
                        </p>
                        {bericht.bemerkungen && <p className="text-xs text-slate-400 mt-1 truncate">{bericht.bemerkungen}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          bericht.status === 'genehmigt' ? 'bg-green-100 text-green-700' :
                          bericht.status === 'eingereicht' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {bericht.status === 'entwurf' ? 'Entwurf' : bericht.status === 'eingereicht' ? 'Eingereicht' : bericht.status === 'genehmigt' ? 'Genehmigt' : bericht.status}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openChildTab('/schnellerfassung/bericht', { title: 'Bericht bearbeiten', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}`, editData: bericht } })}><Edit className="w-4 h-4 mr-2" /> Bearbeiten</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={async () => { if(window.confirm('Bericht löschen?')) { await betreuungAPI.deleteRechnungslegung(bericht.id); toast.success('Gelöscht'); loadData(); }}}><Trash2 className="w-4 h-4 mr-2" /> Löschen</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Noch keine Berichte erstellt</p>
                  <p className="text-xs text-slate-400 mt-1 mb-4">Rechnungslegungen sind jährlich beim Betreuungsgericht einzureichen</p>
                  <Button className="mt-2" onClick={() => openChildTab('/schnellerfassung/bericht', { title: 'Neuer Bericht', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Bericht erstellen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Vergütung */}
        <TabsContent value="verguetung" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <p className="text-slate-500">
              Vergütungsanträge und Auszahlungen (VBVG)
            </p>
            <Button className="w-full sm:w-auto" onClick={() => openChildTab('/schnellerfassung/verguetung', { title: 'Neuer Vergütungsantrag', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })} data-testid="neuer-antrag-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Antrag
            </Button>
          </div>

          {/* Statistik-Karten */}
          {verguetung.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="bg-slate-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-slate-700">{verguetung.length}</p>
                  <p className="text-xs text-slate-500">Anträge</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">
                    {verguetung.filter(a => ['entwurf', 'gestellt'].includes(a.status)).length}
                  </p>
                  <p className="text-xs text-amber-600">Offen</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {verguetung.filter(a => a.status === 'ausgezahlt').reduce((sum, a) => sum + (a.betrag_brutto || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </p>
                  <p className="text-xs text-green-600">Ausgezahlt</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {verguetung.filter(a => ['gestellt', 'bewilligt'].includes(a.status)).reduce((sum, a) => sum + (a.betrag_brutto || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </p>
                  <p className="text-xs text-blue-600">Ausstehend</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardContent className="pt-6">
              {verguetung.length > 0 ? (
                <div className="space-y-3">
                  {verguetung.map(antrag => (
                    <div key={antrag.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors group" data-testid={`antrag-${antrag.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">
                          {antrag.zeitraum_von ? new Date(antrag.zeitraum_von).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' }) : '?'} - {antrag.zeitraum_bis ? new Date(antrag.zeitraum_bis).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' }) : '?'}
                        </p>
                        <p className="text-sm text-slate-500">
                          <span className="font-medium text-slate-700">{(antrag.betrag_brutto || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                          {antrag.betrag_netto && <span className="text-slate-400 ml-2">(netto: {antrag.betrag_netto.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €)</span>}
                        </p>
                        {antrag.auszahlung_datum && (
                          <p className="text-xs text-green-600 mt-1">
                            Ausgezahlt am {new Date(antrag.auszahlung_datum).toLocaleDateString('de-DE')}
                          </p>
                        )}
                        {antrag.bemerkungen && <p className="text-xs text-slate-400 mt-1 truncate">{antrag.bemerkungen}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          antrag.status === 'ausgezahlt' ? 'bg-green-100 text-green-700' :
                          antrag.status === 'bewilligt' ? 'bg-blue-100 text-blue-700' :
                          antrag.status === 'gestellt' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {antrag.status === 'entwurf' ? 'Entwurf' : antrag.status === 'gestellt' ? 'Gestellt' : antrag.status === 'bewilligt' ? 'Bewilligt' : antrag.status === 'ausgezahlt' ? 'Ausgezahlt' : antrag.status}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openChildTab('/schnellerfassung/verguetung', { title: 'Antrag bearbeiten', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}`, editData: antrag } })}><Edit className="w-4 h-4 mr-2" /> Bearbeiten</DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={async () => { if(window.confirm('Antrag löschen?')) { await betreuungAPI.deleteVerguetungsantrag(antrag.id); toast.success('Gelöscht'); loadData(); }}}><Trash2 className="w-4 h-4 mr-2" /> Löschen</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Noch keine Vergütungsanträge</p>
                  <p className="text-xs text-slate-400 mt-1 mb-4">Vergütung nach VBVG (ab 01.01.2026)</p>
                  <Button className="mt-2" onClick={() => openChildTab('/schnellerfassung/verguetung', { title: 'Neuer Vergütungsantrag', forceNew: true, params: { klientId: id, klientName: `${klient.vorname} ${klient.nachname}` } })}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ersten Antrag stellen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Dokumente */}
        <TabsContent value="dokumente" className="space-y-4">
          <DokumentePhysisch klientId={id} />
        </TabsContent>

        {/* Tab: Verbindungen (Klient-Netzwerk) */}
        <TabsContent value="verbindungen">
          <VerbindungenTab
            klientId={id}
            klientName={klient ? `${klient.vorname} ${klient.nachname}` : undefined}
            betreuung={uebersicht?.betreuung}
          />
        </TabsContent>

        {/* ==================== TAB: KI-BERATER ==================== */}
        <TabsContent value="ki-berater">
          <FallKiChat
            klientId={id}
            klientName={klient ? `${klient.vorname} ${klient.nachname}` : 'Betreuter'}
          />
        </TabsContent>
      </Tabs>

      {/* Jahresbericht Dialog */}
      {showBerichtDialog && berichtResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bericht-dialog">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
              <div>
                <h2 className="text-lg font-bold text-slate-800">
                  {berichtResult.typ === 'jahresbericht' ? 'Jahresbericht (§ 1863 BGB)' : 'Rechnungslegung (§ 1865 BGB)'}
                </h2>
                <p className="text-sm text-slate-500">{berichtResult.klient_name}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => handleExportBerichtDocx(berichtResult.bericht_id)}
                  data-testid="bericht-export-docx">
                  <Download className="w-4 h-4 mr-1" /> Word (.docx)
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowBerichtDialog(false)}>
                  <AlertCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                {berichtResult.inhalt}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vorlagen-Bibliothek Dialog */}
      {showVorlagenBibliothek && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
              <h2 className="text-lg font-bold text-slate-800">Vorlagen-Bibliothek</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowVorlagenBibliothek(false)}>
                <AlertCircle className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <VorlagenBibliothek klientId={id} onSelectVorlage={handleVorlageApplied} />
            </div>
          </div>
        </div>
      )}

      {/* Maßnahme Detail/Bearbeiten Dialog */}
      {massnahmeDialog.open && massnahmeDialog.data && (
        <MassnahmeDetailDialog
          data={massnahmeDialog.data}
          klientId={id}
          onClose={() => setMassnahmeDialog({ open: false, data: null })}
          onSaved={() => { setMassnahmeDialog({ open: false, data: null }); loadData(); }}
        />
      )}

    </div>
  );
}


function BuchhaltungMini({ klientId }) {
  const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
  const [konten, setKonten] = useState([]);
  const [stats, setStats] = useState({ konten: 0, umsaetze: 0, ohne_beleg: 0 });
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [kontoOpen, setKontoOpen] = useState(false);
  const fileRef = React.useRef(null);

  const loadData = async () => {
    try {
      const [ko, st] = await Promise.all([
        fetch(`${BACKEND_URL}/api/buchhaltung/konten?klient_id=${klientId}`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/buchhaltung/statistiken/${klientId}`).then(r => r.json()),
      ]);
      setKonten(ko);
      setStats(st);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [klientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || konten.length === 0) return;
    e.target.value = '';

    const formData = new FormData();
    formData.append('datei', file);
    formData.append('konto_id', konten[0].id);
    formData.append('klient_id', klientId);
    formData.append('monat', new Date().toISOString().slice(0, 7));

    try {
      const res = await fetch(`${BACKEND_URL}/api/buchhaltung/kontoauszug-upload`, {
        method: 'POST', body: formData
      }).then(r => r.json());

      if (res.success) {
        toast.success(`${res.umsaetze_count} Umsätze importiert`);
        loadData();
      } else {
        toast.error(res.error || 'Fehler');
      }
    } catch { toast.error('Upload-Fehler'); }
  };

  const handleQuickKonto = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/buchhaltung/konten`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ klient_id: klientId, bezeichnung: 'Girokonto', bank: '', typ: 'girokonto', rechnungslegung_pflicht: true })
      });
      toast.success('Konto angelegt — bitte unter Buchhaltung Details ergänzen');
      loadData();
    } catch { toast.error('Fehler'); }
  };

  if (loading) return <RefreshCw className="w-5 h-5 animate-spin text-slate-400 mx-auto" />;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-2xl font-bold text-slate-800">{stats.konten}</p>
          <p className="text-xs text-slate-500">Konten</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{stats.umsaetze}</p>
          <p className="text-xs text-slate-500">Umsätze</p>
        </div>
        <div>
          <p className={`text-2xl font-bold ${stats.ohne_beleg > 0 ? 'text-amber-600' : 'text-green-600'}`}>{stats.ohne_beleg}</p>
          <p className="text-xs text-slate-500">Ohne Beleg</p>
        </div>
      </div>

      {konten.length > 0 ? (
        <div className="space-y-1.5">
          {konten.map(k => (
            <div key={k.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2">
              <div>
                <span className="font-medium text-slate-700">{k.bezeichnung}</span>
                <span className="text-slate-400 ml-2">{k.bank}</span>
              </div>
              {k.iban && <span className="text-xs text-slate-400 font-mono">{k.iban.slice(-8)}</span>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 text-center">Keine Konten angelegt</p>
      )}

      <div className="flex gap-2">
        {konten.length === 0 ? (
          <Button size="sm" variant="outline" className="flex-1" onClick={handleQuickKonto}>
            <Plus className="w-3 h-3 mr-1" /> Konto anlegen
          </Button>
        ) : (
          <>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleQuickUpload} />
            <Button size="sm" variant="outline" className="flex-1" onClick={() => fileRef.current?.click()}>
              <Upload className="w-3 h-3 mr-1" /> Kontoauszug
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

